import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { HelpCircle, Save, AlertTriangle } from "lucide-react";
import { useState, useEffect } from "react";
import type { CredentialRequest } from "../../types";
import type { District } from "@/models/district";
import type { Region } from "@/models/region";

interface RequestMetadataCardProps {
  request: CredentialRequest | null;
  districtName: string;
  regionName: string;
  ministerCount: number;
  year: number;
  notes: string;
  onNotesChange: (notes: string) => void;
  canEdit: boolean;
  isAdmin: boolean;
  code: string;
  onCodeChange: (code: string) => void;
  onSaveCodeAndNotes?: () => void;
  isSavingCode?: boolean;
  suggestedCodeNumber?: number;
  isInstitutionRequest?: boolean;
  
  // New request mode props (optional)
  isNewRequest?: boolean;
  districts?: District[];
  regions?: Region[];
  availableYears?: number[];
  selectedDistrictId?: string | null;
  selectedRegionId?: string | null;
  selectedYear?: number | null;
  onDistrictChange?: (districtId: string) => void;
  onRegionChange?: (regionId: string) => void;
  onYearChange?: (year: number) => void;
  allDropdownsFilled?: boolean;
}

/**
 * Molecule: Display request metadata information
 */
export function RequestMetadataCard({
  request,
  districtName,
  regionName,
  ministerCount,
  year,
  notes,
  onNotesChange,
  canEdit,
  isAdmin,
  code,
  onCodeChange,
  onSaveCodeAndNotes,
  isSavingCode = false,
  suggestedCodeNumber,
  isInstitutionRequest = false,
  isNewRequest = false,
  districts = [],
  regions = [],
  availableYears = [],
  selectedDistrictId = null,
  selectedRegionId = null,
  selectedYear = null,
  onDistrictChange,
  onRegionChange,
  onYearChange,
  allDropdownsFilled = false,
}: RequestMetadataCardProps) {
  // Local state for code number input
  const [codeNumber, setCodeNumber] = useState<string>("");
  
  // Calculate code prefix based on year
  const yearSuffix = year.toString().slice(-2);
  const codePrefix = `SOL${yearSuffix}-`;
  
  // Initialize code number from existing code or suggested number
  useEffect(() => {
    if (code) {
      // Extract number from existing code (e.g., "SOL26-0450" -> "450")
      const match = code.match(/SOL\d{2}-(\d+)$/);
      if (match) {
        setCodeNumber(match[1]);
      }
    } else if (suggestedCodeNumber !== undefined) {
      // Use suggested number, padded to 3 digits
      setCodeNumber(String(suggestedCodeNumber).padStart(3, '0'));
    }
  }, [code, suggestedCodeNumber]);
  
  // Handle code number input change (only allow numbers, max 3 digits)
  const handleCodeNumberChange = (value: string) => {
    // Only allow digits, max 3 characters
    const numericValue = value.replace(/\D/g, '').slice(0, 3);
    setCodeNumber(numericValue);
    
    // Update parent with full code (without padding yet)
    if (numericValue) {
      onCodeChange(`${codePrefix}${numericValue}`);
    } else {
      onCodeChange('');
    }
  };
  
  // Select all text on focus
  const handleCodeNumberFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    e.target.select();
  };
  
  // Handle blur - format with leading zeros
  const handleCodeNumberBlur = () => {
    if (codeNumber) {
      const paddedNumber = codeNumber.padStart(3, '0');
      setCodeNumber(paddedNumber);
      onCodeChange(`${codePrefix}${paddedNumber}`);
    }
  };

  return (
    <Card className="mb-6">
      <CardHeader>
        <CardTitle className="text-xl">
          {isNewRequest ? 'Nueva Solicitud de Credenciales' : 'Información de la Solicitud'}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Dropdowns for new requests */}
        {isNewRequest && (
          <div className="space-y-4">
            {isInstitutionRequest ? (
              <>
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                  <div className="space-y-2">
                    <Label htmlFor="year-select">Año *</Label>
                    <Select
                      value={selectedYear?.toString() || ''}
                      onValueChange={(value) => onYearChange?.(parseInt(value))}
                    >
                      <SelectTrigger id="year-select">
                        <SelectValue placeholder="Seleccione un año" />
                      </SelectTrigger>
                      <SelectContent>
                        {availableYears.map((y) => (
                          <SelectItem key={y} value={y.toString()}>
                            {y}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {selectedYear && (
                    <div className="space-y-2">
                      <Label htmlFor="sec-code">Código</Label>
                      <Input
                        id="sec-code"
                        value={code}
                        disabled
                        className="bg-muted font-mono text-sm"
                      />
                    </div>
                  )}
                </div>
                {!selectedYear && (
                  <p className="text-sm text-muted-foreground">
                    Seleccione un año para cargar los ministros disponibles.
                  </p>
                )}
              </>
            ) : (
              /* MINISTERIAL: Show all 3 dropdowns */
              <>
                <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                  {/* District dropdown */}
                  <div className="space-y-2">
                    <Label htmlFor="district-select">Distrito *</Label>
                    <Select
                      value={selectedDistrictId || ''}
                      onValueChange={onDistrictChange}
                    >
                      <SelectTrigger id="district-select">
                        <SelectValue placeholder="Seleccione un distrito" />
                      </SelectTrigger>
                      <SelectContent>
                        {districts.map((district) => (
                          <SelectItem key={district.id} value={district.id}>
                            {district.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Region dropdown */}
                  <div className="space-y-2">
                    <Label htmlFor="region-select">Región *</Label>
                    <Select
                      value={selectedRegionId || ''}
                      onValueChange={onRegionChange}
                      disabled={!selectedDistrictId}
                    >
                      <SelectTrigger id="region-select">
                        <SelectValue placeholder="Seleccione una región" />
                      </SelectTrigger>
                      <SelectContent>
                        {regions.map((region) => (
                          <SelectItem key={region.id} value={region.id}>
                            {region.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Year dropdown */}
                  <div className="space-y-2">
                    <Label htmlFor="year-select">Año *</Label>
                    <Select
                      value={selectedYear?.toString() || ''}
                      onValueChange={(value) => onYearChange?.(parseInt(value))}
                    >
                      <SelectTrigger id="year-select">
                        <SelectValue placeholder="Seleccione un año" />
                      </SelectTrigger>
                      <SelectContent>
                        {availableYears.map((y) => (
                          <SelectItem key={y} value={y.toString()}>
                            {y}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                
                {!allDropdownsFilled && (
                  <p className="text-sm text-muted-foreground">
                    Seleccione distrito, región y año para cargar los ministros disponibles.
                  </p>
                )}
              </>
            )}
          </div>
        )}
        
        {/* Code field - show for new MINISTERIAL requests when dropdowns filled, or for existing requests */}
        {!isInstitutionRequest && ((isNewRequest && allDropdownsFilled) || request) ? (
          <div>
            <div className="flex items-center gap-2">
              <Label htmlFor="request-code" className="text-sm font-medium text-muted-foreground">
                Código
              </Label>
              {isAdmin && ((isNewRequest && allDropdownsFilled) || (request && ['DRAFT', 'REJECTED', 'SUBMITTED', 'APPROVED'].includes(request.status || ''))) && (
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <AlertTriangle className="size-3 text-amber-500" />
                    </TooltipTrigger>
                    <TooltipContent>
                      <p className="text-xs">Código editable (solo admin)</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              )}
            </div>
            {/* Editable for admin, read-only for non-admin */}
            {isAdmin ? (
              <div className="mt-1 flex items-center gap-2">
                {/* Split design: prefix + number input */}
                <div className="flex items-center">
                  <span className="inline-flex h-10 items-center rounded-l-md border border-r-0 border-input bg-muted px-3 font-mono text-sm">
                    {codePrefix}
                  </span>
                  <Input
                    id="request-code"
                    value={codeNumber}
                    onChange={(e) => handleCodeNumberChange(e.target.value)}
                    onFocus={handleCodeNumberFocus}
                    onBlur={handleCodeNumberBlur}
                    placeholder="001"
                    className="w-[100px] rounded-l-none font-mono text-sm"
                  />
                </div>
                {request && ['SUBMITTED', 'APPROVED'].includes(request.status || '') && onSaveCodeAndNotes && (
                  <Button
                    onClick={onSaveCodeAndNotes}
                    disabled={isSavingCode || code === (request.code || '')}
                    size="sm"
                    variant="default"
                  >
                    <Save className="mr-1 size-3" />
                    {isSavingCode ? 'Guardando...' : 'Actualizar Código'}
                  </Button>
                )}
              </div>
            ) : (
              /* Read-only split format for non-admin */
              <div className="mt-1 flex items-center">
                <span className="inline-flex h-10 items-center rounded-l-md border border-r-0 border-input bg-muted px-3 font-mono text-sm">
                  {codePrefix}
                </span>
                <Input
                  id="request-code"
                  value={codeNumber}
                  disabled
                  className="w-[100px] rounded-l-none font-mono text-sm"
                />
              </div>
            )}
          </div>
        ) : null}
        
        {/* Request details grid - only for existing requests */}
        {request && (
          <div className="grid grid-cols-2 gap-4 md:grid-cols-3">
            <div>
              <p className="text-sm font-medium text-muted-foreground">Año</p>
              <p className="text-base">{request.year || year}</p>
            </div>
            <div>
              <p className="text-sm font-medium text-muted-foreground">Distrito</p>
              <p className="text-base">{request.district?.name || districtName || '-'}</p>
            </div>
            <div>
              <p className="text-sm font-medium text-muted-foreground">Región</p>
              <p className="text-base">{request.region?.name || regionName || '-'}</p>
            </div>
            <div>
              <p className="text-sm font-medium text-muted-foreground">Ministros</p>
              <p className="text-base">{ministerCount}</p>
            </div>
            <div>
              <p className="text-sm font-medium text-muted-foreground">Fecha de creación</p>
              <p className="text-base">
                {request.created_at 
                  ? new Date(request.created_at).toLocaleDateString('es-BO') 
                  : '-'}
              </p>
            </div>
            {request.submitted_at && (
              <div>
                <p className="text-sm font-medium text-muted-foreground">Fecha de envío</p>
                <p className="text-base">
                  {new Date(request.submitted_at).toLocaleDateString('es-BO')}
                </p>
              </div>
            )}
            {request.approved_at && (
              <div>
                <p className="text-sm font-medium text-muted-foreground">Fecha de aprobación</p>
                <p className="text-base">
                  {new Date(request.approved_at).toLocaleDateString('es-BO')}
                </p>
              </div>
            )}
          </div>
        )}

        {/* Notes field - editable when in draft/pending status */}
        {canEdit && (
          <div className="mt-6">
            <div className="mb-2 flex items-center gap-2">
              <Label htmlFor="request-notes" className="text-sm font-medium">
                Notas (Opcional)
              </Label>
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <HelpCircle className="size-4 cursor-help text-muted-foreground" />
                  </TooltipTrigger>
                  <TooltipContent className="max-w-xs">
                    <p className="text-sm">
                      Use este campo para agregar observaciones importantes. Por ejemplo:
                      "El ministro Juan Pérez fue promovido a Pastor, actualizar grado antes de imprimir la credencial"
                    </p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
            <Textarea
              id="request-notes"
              value={notes}
              onChange={(e) => onNotesChange(e.target.value)}
              placeholder="Agregue notas o observaciones sobre esta solicitud..."
              className="min-h-[80px] resize-none"
              rows={3}
            />
          </div>
        )}

        {/* Display notes for non-editable requests (read-only) */}
        {!canEdit && notes && (
          <div className="mt-6 rounded-lg bg-muted p-4">
            <p className="mb-2 text-sm font-medium">Notas:</p>
            <p className="whitespace-pre-wrap text-sm text-muted-foreground">{notes}</p>
          </div>
        )}

        {/* Observations - only shown when rejecting or when data exists */}
        {request?.observations && (
          <div className="mt-4 rounded-lg bg-red-50 p-4">
            <p className="mb-2 text-sm font-medium text-red-900">Observación:</p>
            <p className="whitespace-pre-wrap text-sm text-red-800">{request.observations}</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
